import { createApi } from '@reduxjs/toolkit/query/react';

import {
  ClientServicesEndpoints as Endpoints,
  type ExportServicesToExcelByIdsRequest,
  type ExportServicesToExcelByIdsResponse,
  type GetBranchClientServicesRequest,
  type GetBranchClientServicesResponse,
  type GetClientServiceByIdRequest,
  type GetClientServiceByIdResponse,
  type GetClientServiceCategoriesForFilterRequest,
  type GetClientServiceCategoriesForFilterResponse,
  type GetClientServiceCategoriesRequest,
  type GetClientServiceCategoriesResponse,
  type GetClientServiceCategoryByIdRequest,
  type GetClientServiceCategoryByIdResponse,
  type GetClientServiceEmployeesRequest,
  type GetClientServiceEmployeesResponse,
  type GetClientServicesByEmployeeIdRequest,
  type GetClientServicesByEmployeeIdResponse,
  type GetClientServicesRequest,
  type GetClientServicesResponse,
  type GetEmployeeClientServicesRequest,
  type GetEmployeeClientServicesResponse,
  type ImportExcelRequest,
  type ImportExcelResponse,
  type SearchClientServicesRequest,
  type SearchClientServicesResponse,
  type SetBranchClientServicesOrderRequest,
  type SetBranchClientServicesOrderResponse,
  type StopImportExcelRequest,
  type StopImportExcelResponse,
  type UnarchiveClientServiceRequest,
  type UnarchiveClientServiceResponse,
  type UpdateClientServiceCategoryRequest,
  type UpdateClientServiceCategoryResponse,
  type UpdateClientServiceEmployeesRequest,
  type UpdateClientServiceEmployeesResponse,
  type UpdateClientServiceRequest,
  type UpdateClientServiceResponse,
  type UpdateEmployeeClientServicesRequest,
  type UpdateEmployeeClientServicesResponse,
} from '@app/api/ClientServices/types';
import { createApiBaseQuery, Methods } from '@app/api/shared';
import { CLIENT_SERVICES_CHUNK } from '@app/constants';

export const clientServicesApi2 = createApi({
  reducerPath: 'clientServicesApi2',
  baseQuery: createApiBaseQuery(CLIENT_SERVICES_CHUNK),
  endpoints: ({ mutation, query }) => ({
    [Endpoints.GetClientServices]: query<
      GetClientServicesResponse,
      GetClientServicesRequest
    >({
      query: (params) => ({
        url: Endpoints.GetClientServices,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetClientServicesByEmployeeId]: query<
      GetClientServicesByEmployeeIdResponse,
      GetClientServicesByEmployeeIdRequest
    >({
      query: (params) => ({
        url: Endpoints.GetClientServicesByEmployeeId,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.SearchClientServices]: mutation<
      SearchClientServicesResponse,
      SearchClientServicesRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SearchClientServices,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetBranchClientServices]: query<
      GetBranchClientServicesResponse,
      GetBranchClientServicesRequest
    >({
      query: ({ logs, ...params }) => ({
        url: Endpoints.GetBranchClientServices,
        method: Methods.GET,
        params,
        paramsSerializer: { indexes: null },
        logs,
      }),
    }),
    [Endpoints.GetClientServiceById]: query<
      GetClientServiceByIdResponse,
      GetClientServiceByIdRequest
    >({
      query: (params) => ({
        url: Endpoints.GetClientServiceById,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetClientServiceCategories]: query<
      GetClientServiceCategoriesResponse,
      GetClientServiceCategoriesRequest
    >({
      query: (params) => ({
        url: Endpoints.GetClientServiceCategories,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetClientServiceCategoriesForFilter]: query<
      GetClientServiceCategoriesForFilterResponse,
      GetClientServiceCategoriesForFilterRequest
    >({
      query: ({ logs, ...params }) => ({
        url: Endpoints.GetClientServiceCategoriesForFilter,
        method: Methods.GET,
        params,
        logs,
      }),
    }),
    [Endpoints.GetClientServiceCategoryById]: query<
      GetClientServiceCategoryByIdResponse,
      GetClientServiceCategoryByIdRequest
    >({
      query: (params) => ({
        url: Endpoints.GetClientServiceCategoryById,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.UpdateClientServiceCategory]: mutation<
      UpdateClientServiceCategoryResponse,
      UpdateClientServiceCategoryRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.UpdateClientServiceCategory,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.UpdateClientService]: mutation<
      UpdateClientServiceResponse,
      UpdateClientServiceRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.UpdateClientService,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.SetBranchClientServicesOrder]: mutation<
      SetBranchClientServicesOrderResponse,
      SetBranchClientServicesOrderRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SetBranchClientServicesOrder,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetEmployeeClientServices]: query<
      GetEmployeeClientServicesResponse,
      GetEmployeeClientServicesRequest
    >({
      query: (params) => ({
        url: Endpoints.GetEmployeeClientServices,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.UpdateEmployeeClientServices]: mutation<
      UpdateEmployeeClientServicesResponse,
      UpdateEmployeeClientServicesRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.UpdateEmployeeClientServices,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetClientServiceEmployees]: query<
      GetClientServiceEmployeesResponse,
      GetClientServiceEmployeesRequest
    >({
      query: (params) => ({
        url: Endpoints.GetClientServiceEmployees,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.UpdateClientServiceEmployees]: mutation<
      UpdateClientServiceEmployeesResponse,
      UpdateClientServiceEmployeesRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.UpdateClientServiceEmployees,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.ExportServicesToExcelByIds]: mutation<
      ExportServicesToExcelByIdsResponse,
      ExportServicesToExcelByIdsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.ExportServicesToExcelByIds,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.ImportExcel]: mutation<ImportExcelResponse, ImportExcelRequest>({
      query: ({ data, ...params }) => ({
        url: Endpoints.ImportExcel,
        method: Methods.POST,
        params,
        data,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    [Endpoints.StopImportExcel]: mutation<
      StopImportExcelResponse,
      StopImportExcelRequest
    >({
      query: (params) => ({
        url: Endpoints.StopImportExcel,
        method: Methods.DELETE,
        params,
      }),
    }),
    [Endpoints.UnarchiveClientService]: mutation<
      UnarchiveClientServiceResponse,
      UnarchiveClientServiceRequest
    >({
      query: (params) => ({
        url: Endpoints.UnarchiveClientService,
        method: Methods.POST,
        params,
      }),
    }),
  }),
});

export const {
  useGetClientServicesQuery,
  useLazyGetClientServicesQuery,
  useGetClientServicesByEmployeeIdQuery,
  useLazyGetClientServicesByEmployeeIdQuery,
  useSearchClientServicesMutation,
  useGetBranchClientServicesQuery,
  useLazyGetBranchClientServicesQuery,
  useGetClientServiceByIdQuery,
  useLazyGetClientServiceByIdQuery,
  useGetClientServiceCategoriesQuery,
  useLazyGetClientServiceCategoriesQuery,
  useGetClientServiceCategoriesForFilterQuery,
  useLazyGetClientServiceCategoriesForFilterQuery,
  useGetClientServiceCategoryByIdQuery,
  useLazyGetClientServiceCategoryByIdQuery,
  useUpdateClientServiceCategoryMutation,
  useUpdateClientServiceMutation,
  useSetBranchClientServicesOrderMutation,
  useGetEmployeeClientServicesQuery,
  useLazyGetEmployeeClientServicesQuery,
  useUpdateEmployeeClientServicesMutation,
  useGetClientServiceEmployeesQuery,
  useLazyGetClientServiceEmployeesQuery,
  useUpdateClientServiceEmployeesMutation,
  useExportServicesToExcelByIdsMutation,
  useImportExcelMutation,
  useStopImportExcelMutation,
  useUnarchiveClientServiceMutation,
} = clientServicesApi2;
